import * as Yup from 'yup';
import * as Validation from './validation';
import React from 'react';
import { FormattedMessage } from 'react-intl';

export const RegisterUserSchema = Yup.object().shape({
  is_organization: Yup.boolean(),
  firstName: Validation.firstNameRules,
  lastName: Validation.lastNameRules,
  email: Validation.emailRules,
  password: Validation.passwordRegisterRules,
  passwordConfirmation: Validation.passwordConfirmationRules,
  terms_and_conditions: Validation.termsAndConditionsRules
});

export const CreateOrganizationSchema = Yup.object().shape({
  name: Validation.organizationNameRules,
  address: Validation.addressRules,
  organization_phone: Validation.organizationPhoneRules,
  companyCountry: Validation.countryRules,
  companyCity: Validation.companyCityRules,
  companyCounty: Validation.companyCountyRules,
  companyCui: Validation.cuiRules,
  companyRegistryNo: Validation.registryNoRules,
});

export const LoginSchema = Yup.object().shape({
  email: Validation.emailRules,
  password: Validation.passwordRules
});

export const ForgotPasswordSchema = Yup.object().shape({
  email: Validation.emailRules
});

export const ResendEmailVerificationSchema = Yup.object().shape({
  email: Validation.emailRules
});

export const ResetPasswordSchema = Yup.object().shape({
  token: Validation.tokenRules,
  password: Validation.passwordRegisterRules,
  password_confirmation: Validation.passwordConfirmationRules
});

export const EditProfileSchema = Yup.object().shape({
  firstName: Validation.firstNameRules,
  lastName: Validation.lastNameRules,
  email: Validation.emailRules
});

export const EditAdminProfileSchema = Yup.object().shape({
  firstName: Validation.firstNameRules.trim(),
  lastName: Validation.lastNameRules.trim(),
  email: Validation.emailRules
});

export const ChangePasswordSchema = Yup.object().shape({
  old_password: Validation.oldPasswordRules,
  new_password: Validation.newPasswordRules,
  new_password_confirmation: Validation.changePasswordConfirmationRules
});

export const CreateUserInOrganizationSchema = Yup.object().shape({
  firstName: Validation.firstNameRules,
  lastName: Validation.lastNameRules,
  email: Validation.emailRules
});

export const CreateOrganizationGroupSchema = Yup.object().shape({
  groupName: Validation.groupNameRules,
});

export const ActivateAccountSchema = Yup.object().shape({
  token: Validation.tokenRules,
  password: Validation.passwordRegisterRules,
  password_confirmation: Validation.passwordConfirmationRules
});

export const CreateDocumentGroupSchema = Yup.object().shape({
  documentGroupName: Validation.documentGroupNameRules,
});

export const CreateUserContactInOrganizationsSchema = Yup.object().shape({
  firstName: Validation.firstNameRules,
  lastName: Validation.lastNameRules,
  email: Validation.emailRules,
  phone: Validation.phoneOptionalRules,
  isPrivate: '',
});

export const SigningMethodSchema = Yup.object().shape({
  manually: Yup.boolean(),
  email: Yup.boolean(),
})
  .test('chooseSigningMethodValidation', null, object => {
    if (object.email || object.manually) {
      return true;
    }

    return new Yup.ValidationError(
      <FormattedMessage id="validation.signature_method_empty"/>,
      null,
      'manually'
    );
  });

export const CreateBillInformationSchema = Yup.object().shape({
  noOfProducts: Validation.noOfProductsRules,
  companyName: Validation.organizationNameRules,
  companyAddress: Validation.addressRules,
  companyCountry: Validation.countryRules,
  companyPhone: Validation.billingPhoneRules,
  companyCity: Validation.companyCityRules,
  companyCounty: Validation.companyCountyRules,
  companyCui: Validation.cuiRules,
  companyRegistryNo: Validation.registryNoRules,
});

export const ContactFormValidationSchema = Yup.object().shape({
  fullName: Validation.fullNameRules,
  email: Validation.emailRules,
  company: Validation.companyRules,
  reason: Validation.reasonRules,
  message: Validation.messageRules,
  terms: Validation.termsRules,
  gdpr: Validation.gdprRules,
  captcha: Validation.captchaRules
});

export const ExpirationDateSchema = Yup.object().shape({
  expiresAtServer: Validation.expireHourRules,
});

export const DeleteAccountSchema = Yup.object().shape({
  password: Validation.passwordRules,
});

export const CreateAdminUserSchema = Yup.object().shape({
  email: Validation.emailRules,
  firstName: Validation.firstNameRules,
  lastName: Validation.lastNameRules,
})


export const VerifyAdminSchema = Yup.object().shape({
  password: Validation.passwordRules,
  password_confirm: Validation.passwordConfirmationRules
});

const createOrderCompanyNameRules = Yup.string()
  .required(<FormattedMessage id="validation.required_organization_name"/>)
  .test(
    'valid-or-selected-organization-name',
    <FormattedMessage id="validation.valid_organization_name"/>,
    function (value) {
      if (! value) {
        return true;
      }

      if (this.parent.selectedOrganizationName === value) {
        return true;
      }

      return Validation.organizationNameRegex.test(value);
    }
  );

export const CreateOrderSchema = Yup.object().shape({
  organization: Validation.organizationSelectRules,
  selectedOrganizationName: Yup.string().nullable(),
  userForPayment: Validation.userForPaymentSelectRules,
  productGroups: Validation.productGroupSelectRules,
  simpleSignaturesNumber: Validation.simpleSignaturesNumber,
  simpleSignaturesValue: Validation.simpleSignaturesPriceValue,
  qualifiedSignaturesNumber: Validation.qualifiedSignaturesNumber,
  qualifiedSignaturesValue: Validation.qualifiedSignaturesPriceValue,
  certificatesNumber: Validation.certificatesNumber,
  certificatesValue: Validation.certificatesPriceValue,
  usersWithCertificate: Validation.userWithCertificatesRules,
  certificatesWithAuthNumber: Validation.certificatesWithAuthNumber,
  certificatesWithAuthValue: Validation.certificatesWithAuthValue,
  usersWithCertificateWithAuth: Validation.userWithCertificatesWithAuthRules,
  companyName: createOrderCompanyNameRules,
  companyAddressLine1: Validation.addressRules,
  companyAddressLine2: Validation.address2Rules,
  companyCountry: Validation.countryRules,
  companyPhone: Validation.billingPhoneRules,
  companyCity: Validation.companyCityRules,
  companyCounty: Validation.companyCountyRules,
  companyCui: Validation.cuiRules,
  companyRegistryNo: Validation.registryNoRules,

});

export const SendEmailClientSchema = Yup.object().shape({
  template: Validation.emailTemplateSelectRules,
  test: Validation.testRules,
  firstName: Validation.firstNameRules,
  email: Validation.emailRules,
});

export const CreateCampaignSchema = Yup.object().shape({
  name: Validation.campaignName,
  notes: Validation.campaignNotes,
  start_date: Validation.campaignStartDate,
  end_date: Validation.campaignEndDate,
  campaign_trackings: Yup.array().of(
    Yup.object().shape({
        tracking_label: Validation.campaignTrackingLabel,
        tracking_id: Validation.campaignTrackingId,
      }),
  ),
});

export const CreateCouponSchema = Yup.object().shape({
  no_of_coupons: Validation.noOfCoupons,
  prefix: Validation.couponPrefix,
  type: Validation.couponType,
  start_date: Validation.campaignStartDate,
  end_date: Validation.campaignEndDate,
  coupon_products: Validation.couponProducts,
});

export const EditCouponSchema = Yup.object().shape({
  prefix: Validation.couponPrefix,
  type: Validation.couponType,
  start_date: Validation.campaignStartDate,
  end_date: Validation.campaignEndDate,
  coupon_products: Validation.couponProducts,
});

export const AddHolidaySchema = Yup.object().shape({
  dayAsTimestamp: Validation.dayAsTimestamp,
  description: Validation.description,
});

export const ChangeSecurityOptionsSchema = Yup.object().shape({
  passkey_auth: Validation.passkeyAuth,
});

export const CreateEmailCampaignSchema = Yup.object().shape({
  template: Validation.emailCampaignTemplateSelectRules,
  recipients_type: Validation.emailCampaignRecipientsSelectRules,
  recipient_test_email: Validation.emailTestRules,
  selected_users: Validation.selectedUserEmailsRules,
});
